export interface IPlaylistCollectionPayload {
  /** The raw playlists array from the server */
  playlists: IPlaylistProps[];
  /** A lookup from song‐usid → display name */
  songIdNames: Record<string, string>;
}

export class PlaylistCollection {
  /** Array of fully instantiated Playlist objects */
  public readonly playlists: Playlist[];
  /** Map from song USID → human‑readable name */
  private readonly _songMap: Map<string, string>;

  constructor(payload: IPlaylistCollectionPayload) {
    // Turn each raw JSON playlist into your Playlist class
    this.playlists = payload.playlists.map(p => new Playlist(p));
    // Build a Map for easy lookup
    this._songMap = new Map(Object.entries(payload.songIdNames));
  }

  /** Get a Playlist by its UniqueID */
  findPlaylist(id: string): Playlist | undefined {
    return this.playlists.find(pl => pl.UniqueID === id);
  }

  /** Get the display name for a song USID */
  getSongName(usID: string): string | undefined {
    return this._songMap.get(usID);
  }

  /** Convenience: get all song USIDs in this collection */
  get allSongIDs(): string[] {
    return Array.from(this._songMap.keys());
  }

  /** Convenience: get all song display names */
  get allSongNames(): string[] {
    return Array.from(this._songMap.values());
  }
}


export interface IPlaylistProps {
  _levelsArray: ILevelProps[];

  _levelIDsArray: string[];

  HasBeenChangedByUser: boolean;

  Source: string;

  UniqueID: string;

  NameLong?: string;

  NameMenu?: string;

  Notes?: string;
}

export class Playlist {
  public readonly UniqueID: string;
  public readonly Source: string;
  public HasBeenChangedByUser: boolean;
  public NameLong?: string;
  public NameMenu?: string;
  public Notes?: string;

  private _levels: Level[];
  private _levelIDs: string[];

  constructor(props: IPlaylistProps) {
    this.UniqueID = props.UniqueID;
    this.Source = props.Source;
    this.HasBeenChangedByUser = props.HasBeenChangedByUser;
    this.NameLong = props.NameLong;
    this.NameMenu = props.NameMenu;
    this.Notes = props.Notes;

    // instantiate Level objects from the raw JSON
    this._levels = props._levelsArray.map((l) => new Level(l));
    this._levelIDs = [...props._levelIDsArray];
  }

  /** Get the array of Level instances */
  get levels(): Level[] {
    return this._levels;
  }

  /** Get the array of level‑IDs */
  get levelIDs(): string[] {
    return this._levelIDs;
  }

  /** Add a new level to the end of this playlist */
  addLevel(level: Level): void {
    this._levels.push(level);
    this._levelIDs.push(level.UniqueID);
    this.HasBeenChangedByUser = true;
  }

  /** Remove a level (and its ID) by its UniqueID */
  removeLevelByID(id: string): boolean {
    const idx = this._levelIDs.indexOf(id);
    if (idx === -1) return false;
    this._levelIDs.splice(idx, 1);
    this._levels.splice(idx, 1);
    this.HasBeenChangedByUser = true;
    return true;
  }

  /** Find a level by its UniqueID */
  findLevel(id: string): Level | undefined {
    return this._levels.find((l) => l.UniqueID === id);
  }
}

export interface ISongSource {
  fullFilePath: string;
  internalResourceName: string;
  relPathWithFilename: string;
  sideloadPathType: number;
  sourceType: number;
  usid: string;
}

export class SongSource implements ISongSource {
  fullFilePath: string;
  internalResourceName: string;
  relPathWithFilename: string;
  sideloadPathType: number;
  sourceType: number;
  usid: string;

  constructor(props: ISongSource) {
    this.fullFilePath = props.fullFilePath;
    this.internalResourceName = props.internalResourceName;
    this.relPathWithFilename = props.relPathWithFilename;
    this.sideloadPathType = props.sideloadPathType;
    this.sourceType = props.sourceType;
    this.usid = props.usid;
  }
}

export interface ILevelProps {
  BaseFilenameNoExt: string;
  CoronavirusMode: boolean;
  Creator: string;
  DateCreated: string; // ISO or localized string
  DateModified: string;
  DifficultyRating: string;
  DirectorMode: number;
  Duration: number;
  DurationString: string;
  GameVersion: string;
  HasBeenChangedByUser: boolean;
  HasBeenSaved: boolean;
  IsAutoSave: boolean;
  IsReady: boolean;
  IsTutorial: boolean;
  IsVizmooExtraLevel: boolean;
  LevelInternalEnum: number;
  LevelSource: number;
  NameLong: string;
  NameMenu: string;
  Notes: string;
  PerformanceCount: number;
  RuntimeLoadPathWithoutFilename: string;
  SongJsonNameDEPRECATED: string;
  SongSource: ISongSource;
  UniqueID: string;
  Volume: number;
}

export class Level implements ILevelProps {
  BaseFilenameNoExt: string;
  CoronavirusMode: boolean;
  Creator: string;
  DateCreated: string;
  DateModified: string;
  DifficultyRating: string;
  DirectorMode: number;
  Duration: number;
  DurationString: string;
  GameVersion: string;
  HasBeenChangedByUser: boolean;
  HasBeenSaved: boolean;
  IsAutoSave: boolean;
  IsReady: boolean;
  IsTutorial: boolean;
  IsVizmooExtraLevel: boolean;
  LevelInternalEnum: number;
  LevelSource: number;
  NameLong: string;
  NameMenu: string;
  Notes: string;
  PerformanceCount: number;
  RuntimeLoadPathWithoutFilename: string;
  SongJsonNameDEPRECATED: string;
  SongSource: SongSource;
  UniqueID: string;
  Volume: number;

  constructor(props: ILevelProps) {
    this.BaseFilenameNoExt = props.BaseFilenameNoExt;
    this.CoronavirusMode = props.CoronavirusMode;
    this.Creator = props.Creator;
    this.DateCreated = props.DateCreated;
    this.DateModified = props.DateModified;
    this.DifficultyRating = props.DifficultyRating;
    this.DirectorMode = props.DirectorMode;
    this.Duration = props.Duration;
    this.DurationString = props.DurationString;
    this.GameVersion = props.GameVersion;
    this.HasBeenChangedByUser = props.HasBeenChangedByUser;
    this.HasBeenSaved = props.HasBeenSaved;
    this.IsAutoSave = props.IsAutoSave;
    this.IsReady = props.IsReady;
    this.IsTutorial = props.IsTutorial;
    this.IsVizmooExtraLevel = props.IsVizmooExtraLevel;
    this.LevelInternalEnum = props.LevelInternalEnum;
    this.LevelSource = props.LevelSource;
    this.NameLong = props.NameLong;
    this.NameMenu = props.NameMenu;
    this.Notes = props.Notes;
    this.PerformanceCount = props.PerformanceCount;
    this.RuntimeLoadPathWithoutFilename = props.RuntimeLoadPathWithoutFilename;
    this.SongJsonNameDEPRECATED = props.SongJsonNameDEPRECATED;
    this.SongSource = new SongSource(props.SongSource);
    this.UniqueID = props.UniqueID;
    this.Volume = props.Volume;
  }

  getDifficultyAsNumber(): number {
    switch (this.DifficultyRating) {
      case "Chill":
        return 1;
      case "Easy":
        return 2;
      case "Regular":
        return 3;
      case "Hard":
        return 4;
      case "Extreme":
        return 5;
      case "Insane":
        return 6;
      default:
        return 0; // Not Set
    }
  }
}
